import type { GameStats } from "../game/Game";

const STYLE = `
.ss-overlay {
  position: fixed; inset: 0; z-index: 10;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 18px; text-align: center; color: #fff; padding: 24px;
  background: radial-gradient(ellipse at 50% 120%, #24427e 0%, #131333 55%, #0a0a1a 100%);
  font-family: "Montserrat", "Helvetica Neue", Arial, sans-serif;
}
.ss-title { font-size: 44px; font-weight: 800; letter-spacing: 2px; margin: 0;
  background: linear-gradient(90deg, #4dd8f0, #e8842c); -webkit-background-clip: text; background-clip: text; color: transparent; }
.ss-sub { font-size: 14px; opacity: 0.75; max-width: 420px; line-height: 1.5; margin: 0; }
.ss-btn {
  font: inherit; font-size: 17px; font-weight: 700; color: #0a0a1a;
  background: linear-gradient(90deg, #4dd8f0, #7ae8c8); border: none; border-radius: 999px;
  padding: 14px 38px; cursor: pointer; letter-spacing: 0.5px;
}
.ss-btn:active { transform: scale(0.96); }
.ss-bar-wrap { width: min(70vw, 360px); height: 10px; border-radius: 999px; background: rgba(255,255,255,0.15); overflow: hidden; }
.ss-bar { height: 100%; width: 0%; border-radius: 999px; background: linear-gradient(90deg, #4dd8f0, #e8842c); transition: width 0.15s; }
.ss-stage { font-size: 13px; opacity: 0.7; min-height: 1.2em; }
.ss-stats { display: grid; grid-template-columns: auto auto; gap: 6px 22px; font-size: 15px; text-align: left; }
.ss-stats b { font-weight: 700; text-align: right; }
.ss-score { font-size: 52px; font-weight: 800; margin: 0; }
.ss-hint { font-size: 12px; opacity: 0.5; }
.ss-rotate { display: none; }
@media (orientation: portrait) {
  .ss-rotate {
    position: fixed; inset: 0; z-index: 50;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px;
    background: #0a0a1a; color: #fff; text-align: center;
    font-family: "Montserrat", "Helvetica Neue", Arial, sans-serif; font-size: 17px; font-weight: 600;
  }
  .ss-rotate span { font-size: 52px; display: inline-block; transform: rotate(90deg); }
}
`;

let styleInjected = false;

function ensureStyle(): void {
  if (styleInjected) return;
  const el = document.createElement("style");
  el.textContent = STYLE;
  document.head.appendChild(el);
  styleInjected = true;
}

function overlay(): HTMLDivElement {
  ensureStyle();
  const el = document.createElement("div");
  el.className = "ss-overlay";
  document.body.appendChild(el);
  return el;
}

export function showTitle(onFile: (file: File) => void): () => void {
  const el = overlay();
  el.innerHTML = `
    <h1 class="ss-title">SOUNDSHOCK</h1>
    <p class="ss-sub">Ride your music. Pick any MP3 on this device — it never leaves your phone. We analyze it and build a track from its energy: climbs, drops, circles and mood.</p>
    <button class="ss-btn" id="ss-pick">Choose a song</button>
    <span class="ss-hint">Best in landscape. Use ▲▼ or arrow keys to switch lanes.</span>
    <input type="file" id="ss-file" accept="audio/*,.mp3,.m4a,.ogg,.wav" style="display:none" />
  `;
  const input = el.querySelector<HTMLInputElement>("#ss-file")!;
  el.querySelector<HTMLButtonElement>("#ss-pick")!.addEventListener("click", () => input.click());
  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file) onFile(file);
  });
  return () => el.remove();
}

export interface LoadingScreen {
  setProgress(stage: string, value: number): void;
  close(): void;
}

export function showLoading(songName: string): LoadingScreen {
  const el = overlay();
  el.innerHTML = `
    <h1 class="ss-title" style="font-size:28px">ANALYZING</h1>
    <p class="ss-sub">${escapeHtml(songName)}</p>
    <div class="ss-bar-wrap"><div class="ss-bar" id="ss-bar"></div></div>
    <span class="ss-stage" id="ss-stage">Reading file…</span>
  `;
  const bar = el.querySelector<HTMLDivElement>("#ss-bar")!;
  const stageEl = el.querySelector<HTMLSpanElement>("#ss-stage")!;
  return {
    setProgress(stage, value) {
      bar.style.width = `${Math.round(value * 100)}%`;
      stageEl.textContent = stage;
    },
    close: () => el.remove(),
  };
}

export function showReady(
  songName: string,
  bpm: number,
  noteCount: number,
  seed: number,
  onStart: () => void,
): () => void {
  const el = overlay();
  el.innerHTML = `
    <h1 class="ss-title" style="font-size:30px">TRACK READY</h1>
    <p class="ss-sub">${escapeHtml(songName)} · ~${bpm} BPM · ${noteCount} circles</p>
    <span class="ss-hint">seed ${seed.toString(16).padStart(8, "0")}</span>
    <button class="ss-btn" id="ss-start">Ride</button>
  `;
  el.querySelector<HTMLButtonElement>("#ss-start")!.addEventListener("click", () => onStart());
  return () => el.remove();
}

export function showResults(songName: string, stats: GameStats, onRestart: () => void): () => void {
  const el = overlay();
  const pct = stats.totalNotes > 0 ? Math.round((stats.collected / stats.totalNotes) * 100) : 0;
  el.innerHTML = `
    <p class="ss-sub">${escapeHtml(songName)}</p>
    <p class="ss-score">${stats.score.toLocaleString("de-DE")}</p>
    <div class="ss-stats">
      <span>Circles</span><b>${stats.collected} / ${stats.totalNotes} (${pct}%)</b>
      <span>Combos</span><b>${stats.combos}</b>
      <span>Full rows</span><b>${stats.fullRows}</b>
      <span>Missed</span><b>${stats.missed}</b>
    </div>
    <button class="ss-btn" id="ss-again">Ride another song</button>
  `;
  el.querySelector<HTMLButtonElement>("#ss-again")!.addEventListener("click", () => onRestart());
  return () => el.remove();
}

/** Fullscreen "rotate your phone" gate, visible only in portrait. */
export function showRotateGate(): () => void {
  ensureStyle();
  const el = document.createElement("div");
  el.className = "ss-rotate";
  el.innerHTML = `<span>\u{1F4F1}</span>Rotate your phone to ride`;
  document.body.appendChild(el);
  return () => el.remove();
}

function escapeHtml(s: string): string {
  const div = document.createElement("div");
  div.textContent = s;
  return div.innerHTML;
}
